import { logger } from '@/shared/utils';
/**
 * Yields control back to the main thread to prevent UI lockup.
 */
export const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));

/**
 * Defers execution until the browser is idle or a timeout occurs.
 * @param {Function} task - The task to execute.
 * @param {number} timeout - Maximum time to wait before forcing execution.
 */
export const deferExecution = (task, timeout = 2000) => {
   return new Promise(resolve => {
      const wrappedTask = async () => {
         await task();
         resolve();
      };

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
         window.requestIdleCallback(wrappedTask, { timeout });
      } else {
         setTimeout(wrappedTask, 0);
      }
   });
};

/**
 * Limited concurrency async pool helper.
 * @param {number} poolLimit - Max number of concurrent executions.
 * @param {Array} array - Items to process.
 * @param {Function} iteratorFn - Function to run for each item.
 */
export const asyncPool = async (poolLimit, array, iteratorFn) => {
   const ret = [];
   const executing = [];
   for (const item of array) {
      const p = Promise.resolve().then(() => iteratorFn(item));
      ret.push(p);

      if (poolLimit <= array.length) {
         const e = p.then(() => executing.splice(executing.indexOf(e), 1));
         executing.push(e);
         if (executing.length >= poolLimit) {
            await Promise.race(executing);
         }
      }
   }
   return Promise.all(ret);
};

/**
 * Calculates local progress within a specific range.
 * @param {number} current - Current item index (0-based).
 * @param {number} total - Total number of items.
 * @param {number} startRange - Progress start (0-100).
 * @param {number} endRange - Progress end (0-100).
 * @returns {number} Calculated progress value.
 */
export const calculateProgress = (current, total, startRange, endRange) => {
   if (total === 0) return endRange;
   const range = endRange - startRange;
   return startRange + Math.round(((current + 1) / total) * range);
};

const SYSTEM_MESSAGE_TYPES = new Set(['1', '2', '3', '4', '5']);

/**
 * Filter messages to exclude system messages and deleted messages.
 * @param {Array} messages - List of messages to filter.
 */
export const filterMessages = messages => {
   const result = [];
   for (const msg of messages) {
      if (!SYSTEM_MESSAGE_TYPES.has(msg.messageType) && !msg.isDeletedForEveryone && !msg.isDeletedForMe) {
         result.push({ ...msg, type: 'message' });
      }
   }
   return result;
};

/**
 * Creates a module-specific logger with batching and grouped console output.
 * @param {string} moduleName - Name of the module (e.g., 'ChatSync').
 * @returns {Object} Logger object with log, logTime, and flushLogs methods.
 */
export const createLogger = moduleName => {
   let logs = [];

   return {
      log: (id, message, ...args) => {
         const details = args.length > 0 ? ` : ${JSON.stringify(args)}` : '';
         logs.push(`[${id}] ${message}${details}`);
      },

      logTime: (id, label, startTime) => {
         const duration = ((performance.now() - startTime) / 1000).toFixed(2);
         logs.push(`[${id}] ${label} (${duration}s)`);
         return duration;
      },

      flushLogs: (title, success = true) => {
         if (logs.length === 0) return;
         const status = success ? '✅' : '❌';
         const header = `${status} [${moduleName}] ${title}`;

         logger.debug(header);
         logs.forEach(l => logger.log(l));
         logger.debug('--');

         logs = [];
      },
   };
};
